import React, { useState, useEffect } from 'react';
import './styles/global.css';
import { useStore }         from './hooks/useStore';
import { store }            from './utils/store';
import { supabase }         from './utils/supabase';
import { Login, Register, ResetSenha } from './components/UI/Auth';
import { Sidebar }          from './components/Sidebar/Sidebar';
import { Layout }           from './components/UI/Layout';
import { Portal }           from './components/Portal/Portal';
import { Dashboard }        from './components/Dashboard/Dashboard';
import { Agendamentos }     from './components/Agendamentos/Agendamentos';
import { Finalizados }      from './components/Finalizados/Finalizados';
import { Clientes }         from './components/Clientes/Clientes';
import { Servicos }         from './components/Servicos/Servicos';
import { Profissionais }    from './components/Profissionais/Profissionais';
import { Horarios }         from './components/Horarios/Horarios';
import { LinkAgendamento }  from './components/Link/LinkAgendamento';
import { Assinatura }       from './components/Assinatura/Assinatura';
import { Perfil }           from './components/Perfil/Perfil';

const params = new URLSearchParams(window.location.search);
const C_TID  = params.get('c');

export default function App() {
  const storeState = useStore();
  const [authMode, setAuthMode] = useState('login');
  const [page,     setPage]     = useState('dashboard');
  const [sidebar,  setSidebar]  = useState(false);
  const [booting,  setBooting]  = useState(true);
  const [isReset,  setIsReset]  = useState(false); // detectado via evento Supabase

  useEffect(() => {
    // Escuta eventos de autenticação do Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth event:', event, 'session:', session?.user?.email);

      if (event === 'PASSWORD_RECOVERY') {
        // Supabase processou o link e está pronto para redefinir senha
        console.log('✅ PASSWORD_RECOVERY detectado — mostrando tela de reset');
        setIsReset(true);
        setBooting(false);
        return;
      }

      if (event === 'SIGNED_IN' && isReset) {
        // Ignora SIGNED_IN se estiver no fluxo de reset
        return;
      }
    });

    // Inicia sessão normal
    store.initSession().finally(() => {
      setBooting(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Portal do cliente via ?c=adminId
  if (C_TID) return <Portal tid={C_TID} />;

  // Tela de reset — detectada via evento PASSWORD_RECOVERY
  if (isReset) return (
    <ResetSenha onDone={() => {
      setIsReset(false);
      window.location.replace('/');
    }} />
  );

  if (booting) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:14, color:'#aaa' }}>
      Carregando...
    </div>
  );

  const { user, tenant, logo, loading, acesso } = storeState;

  if (!user || !tenant) {
    return authMode === 'login'
      ? <Login    onLogin={() => {}} onRegister={() => setAuthMode('register')} />
      : <Register onLogin={() => {}} onBack={() => setAuthMode('login')} />;
  }

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:14, color:'#aaa' }}>
      Carregando dados...
    </div>
  );

  if (acesso && !acesso.tem_acesso) {
    return (
      <Assinatura
        adminId={user.id}
        email={user.email}
        empresa={tenant.empresa}
      />
    );
  }

  const handleLogout = () => store.logout();
  const handleLogo   = (fileOrNull) => store.setLogo(fileOrNull);

  const renderPage = () => {
    switch (page) {
      case 'dashboard':     return <Dashboard onNav={setPage} />;
      case 'agendamentos':  return <Agendamentos />;
      case 'finalizados':   return <Finalizados />;
      case 'clientes':      return <Clientes />;
      case 'servicos':      return <Servicos />;
      case 'profissionais': return <Profissionais />;
      case 'horarios':      return <Horarios />;
      case 'link':          return <LinkAgendamento />;
      case 'assinatura':    return <Assinatura adminId={user.id} email={user.email} empresa={tenant.empresa} />;
      case 'perfil':        return <Perfil />;
      default:              return <Dashboard />;
    }
  };

  return (
    <div style={{ display:'flex', width:'100%', minHeight:'100vh' }}>
      <Sidebar
        user={user}
        page={page}
        onNav={setPage}
        onLogout={handleLogout}
        open={sidebar}
        onClose={() => setSidebar(false)}
        logo={logo}
        onLogoChange={handleLogo}
        acesso={acesso}
      />
      <Layout user={user} logo={logo} page={page} onMenu={() => setSidebar(o => !o)}>
        {acesso?.status === 'trial' && acesso?.dias_trial_restantes <= 5 && (
          <div style={{
            background: acesso.dias_trial_restantes <= 1 ? '#fef2f2' : '#fffbeb',
            borderBottom: `0.5px solid ${acesso.dias_trial_restantes <= 1 ? '#fecaca' : '#fde68a'}`,
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 13,
            color: acesso.dias_trial_restantes <= 1 ? '#dc2626' : '#92400e',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <span>
              {acesso.dias_trial_restantes === 0
                ? '⚠️ Seu período gratuito termina hoje!'
                : `⏳ ${acesso.dias_trial_restantes} dia${acesso.dias_trial_restantes > 1 ? 's' : ''} restante${acesso.dias_trial_restantes > 1 ? 's' : ''} no período gratuito`
              }
            </span>
            <button
              onClick={() => setPage('assinatura')}
              style={{
                background: acesso.dias_trial_restantes <= 1 ? '#dc2626' : '#d97706',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '6px 16px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}
            >
              Assinar agora
            </button>
          </div>
        )}
        {renderPage()}
      </Layout>
    </div>
  );
}
